import { addDays, diffDays, type TenantSettings } from '@dairy/shared';
import { eventsOf } from './events';
import { currentLactation, deriveLactations } from './lactation';
import { enabledSessions } from './milk';
import { deriveReproStatus } from './repro';
import { deriveStage } from './stage';
import { live, type DomainAnimal, type DomainEvent, type DomainMilkRecord } from './types';

/** D-12 Wood curve: y(t) = a · t^b · e^(−c·t), with t = max(DIM, 1). */
export function woodY(a: number, t: number, b: number, c: number): number {
  const tt = Math.max(t, 1);
  return a * Math.pow(tt, b) * Math.exp(-c * tt);
}

/** Scale `a` so the curve passes through (tObs, yObs). */
export function woodA(yObs: number, tObs: number, b: number, c: number): number {
  const tt = Math.max(tObs, 1);
  return yObs / (Math.pow(tt, b) * Math.exp(-c * tt));
}

/** Scale `a` so the curve's peak y(b/c) equals `peak`. */
export function woodAForPeak(peak: number, b: number, c: number): number {
  return woodA(peak, b / c, b, c);
}

export interface WoodFit {
  a: number;
  b: number;
  c: number;
  r2: number;
}

/** Solve a 3×3 linear system (Gaussian elimination with partial pivoting). */
function solve3(m: number[][], v: number[]): number[] | null {
  const A = m.map((row, i) => [...row, v[i]!]);
  for (let col = 0; col < 3; col++) {
    let pivot = col;
    for (let r = col + 1; r < 3; r++) if (Math.abs(A[r]![col]!) > Math.abs(A[pivot]![col]!)) pivot = r;
    if (Math.abs(A[pivot]![col]!) < 1e-12) return null;
    [A[col], A[pivot]] = [A[pivot]!, A[col]!];
    for (let r = 0; r < 3; r++) {
      if (r === col) continue;
      const f = A[r]![col]! / A[col]![col]!;
      for (let k = col; k < 4; k++) A[r]![k]! -= f * A[col]![k]!;
    }
  }
  return [A[0]![3]! / A[0]![0]!, A[1]![3]! / A[1]![1]!, A[2]![3]! / A[2]![2]!];
}

/**
 * MP-3 / D-12: least-squares fit of ln y = ln a + b·ln t − c·t on points with t ≥ 5 and y > 0.
 * Returns null unless 0.05 ≤ b ≤ 0.6, 0.0005 ≤ c ≤ 0.02 and R² ≥ 0.5.
 */
export function fitWood(points: ReadonlyArray<{ t: number; y: number }>): WoodFit | null {
  const pts = points.filter((p) => p.t >= 5 && p.y > 0);
  if (pts.length < 3) return null;
  // Columns: 1, ln t, −t  → coefficients: ln a, b, c
  const X = pts.map((p) => [1, Math.log(p.t), -p.t]);
  const Y = pts.map((p) => Math.log(p.y));
  const XtX = [0, 1, 2].map((i) => [0, 1, 2].map((j) => X.reduce((s, row) => s + row[i]! * row[j]!, 0)));
  const XtY = [0, 1, 2].map((i) => X.reduce((s, row, k) => s + row[i]! * Y[k]!, 0));
  const beta = solve3(XtX, XtY);
  if (!beta) return null;
  const [lnA, b, c] = beta as [number, number, number];
  const meanY = Y.reduce((s, y) => s + y, 0) / Y.length;
  let ssRes = 0;
  let ssTot = 0;
  X.forEach((row, k) => {
    const pred = lnA * row[0]! + b * row[1]! + c * row[2]!;
    ssRes += (Y[k]! - pred) ** 2;
    ssTot += (Y[k]! - meanY) ** 2;
  });
  const r2 = ssTot === 0 ? (ssRes < 1e-12 ? 1 : 0) : 1 - ssRes / ssTot;
  if (!(b >= 0.05 && b <= 0.6 && c >= 0.0005 && c <= 0.02 && r2 >= 0.5)) return null;
  return { a: Math.exp(lnA), b, c, r2 };
}

type ForecastSettings = Pick<
  TenantSettings,
  | 'sessions'
  | 'calfMaxAgeMonths'
  | 'gestationDays'
  | 'dryPeriodDays'
  | 'defaultPeakLitersPerDay'
  | 'firstLactationFactor'
  | 'woodB'
  | 'woodC'
>;

export interface CowForecast {
  animalId: string;
  enoughData: boolean;
  curveSource: 'DEFAULT' | 'PERSONAL';
  /** Daily liters (ml, rounded) for each forecast day. */
  daily: Array<{ date: string; ml: number }>;
  totalMl: number;
  avgPerDayMl: number | null;
}

/** Daily totals per date for one cow. */
function dailyTotalsByDate(records: readonly DomainMilkRecord[], animalId: string): Map<string, { ml: number; sessions: Set<string> }> {
  const m = new Map<string, { ml: number; sessions: Set<string> }>();
  for (const r of live(records)) {
    if (r.animalId !== animalId) continue;
    const d = m.get(r.date) ?? { ml: 0, sessions: new Set<string>() };
    d.ml += r.ml;
    d.sessions.add(r.session);
    m.set(r.date, d);
  }
  return m;
}

/** Highest rolling 7-day average of daily totals within [from, to] (ml/day), or null. */
export function peak7DayAverage(
  records: readonly DomainMilkRecord[],
  animalId: string,
  from: string,
  to: string,
): number | null {
  const byDate = dailyTotalsByDate(records, animalId);
  let best: number | null = null;
  for (let d = from; d <= to; d = addDays(d, 1)) {
    const end = addDays(d, 6);
    if (end > to) break;
    let sum = 0;
    let days = 0;
    for (let k = d; k <= end; k = addDays(k, 1)) {
      const v = byDate.get(k);
      if (v) {
        sum += v.ml;
        days += 1;
      }
    }
    if (days >= 5) {
      const avg = sum / days;
      if (best === null || avg > best) best = avg;
    }
  }
  return best;
}

/** MP-3: personal curve from the most recent completed lactation with ≥ 200 days of records. */
export function personalCurve(
  animal: DomainAnimal,
  events: readonly DomainEvent[],
  records: readonly DomainMilkRecord[],
): WoodFit | null {
  const lacts = deriveLactations(animal, events).filter((l) => l.endDate !== null);
  const byDate = dailyTotalsByDate(records, animal.id);
  for (let i = lacts.length - 1; i >= 0; i--) {
    const l = lacts[i]!;
    const points: Array<{ t: number; y: number }> = [];
    for (const [date, v] of byDate) {
      if (date >= l.calvingDate && date <= l.endDate!) points.push({ t: diffDays(date, l.calvingDate), y: v.ml / 1000 });
    }
    if (points.length < 200) continue;
    const fit = fitWood(points);
    if (fit) return fit;
  }
  return null;
}

/**
 * MP-1 / D-12: one cow's forecast for `days` days starting on `start`.
 * Observation window: the 7 days before `start` with every enabled session recorded (≥ 3 needed).
 * Milking cows follow their curve until the dry-off due date (PREGNANT) or until y < 1.0 L;
 * a cow or heifer with an expected calving in the horizon starts a new lactation on that date.
 */
export function cowForecast(
  cow: DomainAnimal,
  events: readonly DomainEvent[],
  records: readonly DomainMilkRecord[],
  s: ForecastSettings,
  start: string,
  days: number,
  options: { fallbackToDefault?: boolean } = {},
): CowForecast {
  const evs = eventsOf(events, cow.id);
  const personal = personalCurve(cow, evs, records);
  const b = personal?.b ?? s.woodB;
  const c = personal?.c ?? s.woodC;
  const curveSource: CowForecast['curveSource'] = personal ? 'PERSONAL' : 'DEFAULT';
  const asOf = addDays(start, -1);
  const stage = deriveStage(cow, evs, asOf, s);
  const repro = deriveReproStatus(cow, evs, asOf, s);
  const lact = currentLactation(cow, evs, asOf);

  // Observation for a milking cow.
  let aCurrent: number | null = null;
  let enoughData = true;
  if (stage === 'MILKING' && lact) {
    const needed = enabledSessions(s).map((x) => x.session);
    const byDate = dailyTotalsByDate(records, cow.id);
    const obs: Array<{ y: number; t: number }> = [];
    for (let k = 7; k >= 1; k--) {
      const d = addDays(start, -k);
      if (d < lact.calvingDate) continue;
      const v = byDate.get(d);
      if (v && needed.every((n) => v.sessions.has(n))) obs.push({ y: v.ml / 1000, t: diffDays(d, lact.calvingDate) });
    }
    if (obs.length >= 3) {
      const yObs = obs.reduce((acc, o) => acc + o.y, 0) / obs.length;
      const tObs = obs.reduce((acc, o) => acc + o.t, 0) / obs.length;
      aCurrent = woodA(yObs, tObs, b, c);
    } else {
      enoughData = false;
      if (options.fallbackToDefault) {
        const peak = s.defaultPeakLitersPerDay * (lact.number <= 1 ? s.firstLactationFactor : 1);
        aCurrent = woodAForPeak(peak, b, c);
      }
    }
  }

  // Next lactation inside the horizon.
  const nextCalving = repro?.status === 'PREGNANT' ? repro.expectedCalving : undefined;
  const dryDue = repro?.status === 'PREGNANT' ? repro.dryOffDue : undefined;
  let aNext: number | null = null;
  if (nextCalving) {
    const lastLact = deriveLactations(cow, evs).filter((l) => l.calvingDate <= asOf).pop();
    let peak: number | null = null;
    if (lastLact) {
      const p = peak7DayAverage(records, cow.id, lastLact.calvingDate, lastLact.endDate ?? asOf);
      peak = p !== null ? p / 1000 : null;
    }
    if (peak === null) {
      peak = s.defaultPeakLitersPerDay * (lastLact ? 1 : s.firstLactationFactor);
    }
    aNext = woodAForPeak(peak, b, c);
  }

  const daily: Array<{ date: string; ml: number }> = [];
  let total = 0;
  let stopped = false; // current lactation ended (dry or below 1 L)
  for (let i = 0; i < days; i++) {
    const date = addDays(start, i);
    let liters = 0;
    if (nextCalving && aNext !== null && date >= nextCalving) {
      liters = woodY(aNext, diffDays(date, nextCalving), b, c);
    } else if (aCurrent !== null && lact && !stopped) {
      if (dryDue && date > dryDue) stopped = true;
      else {
        const y = woodY(aCurrent, diffDays(date, lact.calvingDate), b, c);
        if (!dryDue && y < 1.0) stopped = true;
        else liters = y;
      }
    }
    const ml = Math.round(liters * 1000);
    daily.push({ date, ml });
    total += ml;
  }
  return {
    animalId: cow.id,
    enoughData,
    curveSource,
    daily,
    totalMl: total,
    avgPerDayMl: days > 0 ? Math.round(total / days) : null,
  };
}

export interface HerdForecast {
  daily: Array<{ date: string; ml: number }>;
  cows: CowForecast[];
  total30Ml: number;
  total90Ml: number;
  totalMl: number;
}

/** MP-2 / D-12: herd forecast = sum over all active females per day. */
export function herdForecast(
  animals: readonly DomainAnimal[],
  events: readonly DomainEvent[],
  records: readonly DomainMilkRecord[],
  s: ForecastSettings,
  start: string,
  days: number,
): HerdForecast {
  const asOf = addDays(start, -1);
  const females = live(animals).filter((a) => a.sex === 'FEMALE' && a.status === 'ACTIVE');
  const cows: CowForecast[] = [];
  for (const a of females) {
    const f = cowForecast(a, events, records, s, start, days, { fallbackToDefault: true });
    if (f.totalMl > 0 || deriveStage(a, events, asOf, s) === 'MILKING') cows.push(f);
  }
  const daily = Array.from({ length: days }, (_, i) => ({
    date: addDays(start, i),
    ml: cows.reduce((acc, cf) => acc + (cf.daily[i]?.ml ?? 0), 0),
  }));
  const sumFirst = (n: number) => daily.slice(0, n).reduce((acc, d) => acc + d.ml, 0);
  return {
    daily,
    cows,
    total30Ml: sumFirst(30),
    total90Ml: sumFirst(90),
    totalMl: sumFirst(days),
  };
}

/** D-11 forecast accuracy = 1 − |forecast − actual| ÷ actual. */
export function forecastAccuracy(forecastMl: number, actualMl: number): number | null {
  if (actualMl <= 0) return null;
  return 1 - Math.abs(forecastMl - actualMl) / actualMl;
}
